import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, getTrackByFn } from '@y42/angular/util';
import { SideDetailViewService } from '@y42/core/container-with-side-detail-view';
import { DataTestType } from '@y42/data-test';
import { ColumnTypeEnum } from '@y42/json-schema';
import { isNotNullish, isNullish } from '@y42/util/nullish';
import { combineLatest, Observable, of, startWith } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { DataTestBuilderFormService } from '../../data-test-builder-form.service';
import {
  DataTestBuilderSaveOutput,
  DataTestBuilderSidebarInput,
  isEditDataTest,
  isNewDataTest,
} from '../../data-test-builder.model';

interface ColumnSelectUIModel {
  column: string;
  type: ColumnTypeEnum | null;
}

@UntilDestroy()
@Component({
  selector: 'y42-unique-test',
  templateUrl: './unique-test.component.html',
  styleUrls: ['./unique-test.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UniqueTestComponent implements OnInit {
  constructor(
    private readonly dataTestBuilderFormService: DataTestBuilderFormService,
    private readonly sideDetailViewService: SideDetailViewService<
      DataTestBuilderSaveOutput,
      DataTestBuilderSidebarInput
    >,
  ) {}

  readonly formGroup = this.dataTestBuilderFormService.getFormByTestType(DataTestType.Unique);

  private readonly columns$: Observable<ColumnSelectUIModel[]> = combineLatest([
    this.sideDetailViewService.data$,
    this.formGroup.controls.column.valueChanges.pipe(startWith(this.formGroup.controls.column.value)),
  ]).pipe(
    map(([sideBarData, formControlValue]) => {
      if (isNullish(sideBarData)) {
        return [];
      }
      const schemaTest = sideBarData.dataTests.find((dt) => dt.type === DataTestType.Schema);
      let allColumns: ColumnSelectUIModel[] = [];
      if (isNotNullish(schemaTest) && schemaTest.type === DataTestType.Schema) {
        allColumns = schemaTest.schema.map((item): { column: string; type: ColumnTypeEnum } => ({
          column: item.column,
          type: item.type,
        }));
      }

      // filter columns that already used in other "unique" tests (for the new test)
      const dataTestsToIterate = isNewDataTest(sideBarData) ? sideBarData.dataTests : [];
      for (const dataTest of dataTestsToIterate) {
        if (dataTest.type !== DataTestType.Unique) {
          continue;
        }
        const column = dataTest.column;
        allColumns = allColumns.filter((x) => x.column !== column);
      }

      // in case the column was deleted from the schema test
      if (formControlValue && !allColumns.find((x) => x.column === formControlValue)) {
        allColumns.push({ column: formControlValue, type: null });
      }

      return allColumns;
    }),
  );

  readonly state = connectState(this, {
    columns: this.columns$,
    saveButtonDisabled: this.columns$.pipe(map((columns) => columns.length === 0)),
    selectedColumnType: this.columns$.pipe(
      switchMap((columns) =>
        combineLatest([
          this.formGroup.controls.column.valueChanges.pipe(startWith(this.formGroup.controls.column.value)),
          of(columns),
        ]),
      ),
      map(([colName, columns]) => {
        return columns.find((x) => x.column === colName)?.type ?? null;
      }),
    ),
  });

  readonly trackByColumn = getTrackByFn<ColumnSelectUIModel>('column');

  ngOnInit(): void {
    this.disableColumnDropDownOnCondition();
  }

  disableColumnDropDownOnCondition(): void {
    this.sideDetailViewService.data$
      .pipe(
        tap((sidebarData) => {
          const formGroup = this.dataTestBuilderFormService.getFormByTestType(DataTestType.Unique);
          const disable = sidebarData ? isEditDataTest(sidebarData) : true;
          formGroup.controls.column[disable ? 'disable' : 'enable']({ emitEvent: false });
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }
}
